import {
  GhostButton,
  PrimaryButton,
  SecondaryTopNavWithStepper,
} from "@certn/cascadia-react";
import { t } from "i18next";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import { PackageContext } from "../PackageContext";
import { useFlags } from "launchdarkly-react-client-sdk";
import { shouldShowClientInputConfiguration } from "../package-utils";

interface Props {
  onComplete?: () => void;
  isDisabled?: boolean;
  submitting?: boolean;
}

export function PackageStepHeader({
  onComplete,
  isDisabled,
  submitting,
}: Props) {
  const navigate = useNavigate();
  const { goToPrevStep, currentStep, packageData } = useContext(PackageContext);
  const cancel = () => {
    navigate("/packages");
  };

  const { enableClientAdjudication } = useFlags();
  const shouldShowConfiguration = packageData
    ? shouldShowClientInputConfiguration(packageData)
    : false;

  const steps = [
    t("Package details"),
    t("Checks"),
    ...(enableClientAdjudication ? [t("Set check score")] : []),
    ...(shouldShowConfiguration ? [t("Configuration")] : []),
    t("Review"),
  ];

  const atLastStep = currentStep >= steps.length - 1;

  return (
    <SecondaryTopNavWithStepper
      testID="package-page-top-nav"
      stepperSteps={steps.map((step, index) => ({
        title: step,
        status: index < currentStep ? "COMPLETE" : "INCOMPLETE",
      }))}
      handlePrevStep={goToPrevStep}
      handleClose={cancel}
      currentStepIndex={currentStep}
    >
      <GhostButton onPress={cancel} testID="package-page-top-nav-cancel-button">
        {t("Cancel")}
      </GhostButton>
      {!atLastStep ? (
        <PrimaryButton
          onPress={onComplete}
          isDisabled={isDisabled}
          isLoading={submitting}
          testID="package-page-top-nav-next-step-button"
        >
          {t("Next step")}
        </PrimaryButton>
      ) : (
        <PrimaryButton
          onPress={onComplete}
          isDisabled={isDisabled}
          isLoading={submitting}
          testID="package-page-top-nav-send-package-button"
        >
          {t("Save package")}
        </PrimaryButton>
      )}
    </SecondaryTopNavWithStepper>
  );
}
